// File: app/src/audio/scheduleCue.js
//
// Schedules one cue of a sound set onto the audio clock. Shared by useSound
// and the offline loudness harness, so what gets measured is what plays.
//
// A set's `voices` name its instruments: `synth` and `fm` are Tone.js
// PolySynths; anything else is one of the hand-built INSTRUMENTS. A note picks
// its voice with `voice` (default "main") and may carry vel, damp, roll or
// bend for the instrument. Tone is passed in, so this module never loads it.

import { INSTRUMENTS } from "./instruments";

export function isValidSequenceStep(step) {
  return (
    !!step &&
    ((Object.prototype.hasOwnProperty.call(step, "note") && typeof step.duration === "number") ||
      (Object.prototype.hasOwnProperty.call(step, "rest") && typeof step.rest === "number"))
  );
}

// One output per voice of a set, built on first use and kept in `cache`:
// a PolySynth for synth/fm voices, otherwise a gain the instrument plays into.
function voiceOutput(Tone, cache, destination, setKey, voiceKey, voice) {
  const key = `${setKey}:${voiceKey}`;
  if (!cache.has(key)) {
    if (voice.instrument === "synth" || voice.instrument === "fm") {
      const synth = new Tone.PolySynth(voice.instrument === "fm" ? Tone.FMSynth : Tone.Synth, {
        // High enough that two overlapping multi-note cues (e.g. "resume"
        // plus the first count-in tick) never exceed polyphony — Tone drops
        // notes past the cap, which is audible as garbled playback.
        maxPolyphony: 12,
        ...voice.options,
      }).connect(destination);
      synth.volume.value = voice.volume ?? 0;
      cache.set(key, { synth });
    } else {
      const gain = Tone.getContext().createGain();
      gain.gain.value = Tone.dbToGain(voice.volume ?? 0);
      Tone.connect(gain, destination);
      cache.set(key, { gain });
    }
  }
  return cache.get(key);
}

/** Schedules `type` from the set at Tone time `at`; returns when its notes end. */
export function scheduleCue({ Tone, setKey, profile, type, at, destination, cache }) {
  const steps = profile?.sounds?.[type];
  if (!Array.isArray(steps)) return at;
  const ctx = Tone.getContext();
  let now = at;

  steps.forEach((step) => {
    if (!isValidSequenceStep(step)) return;
    if (Object.prototype.hasOwnProperty.call(step, "rest")) {
      now += step.rest;
      return;
    }
    const voiceKey = step.voice || "main";
    const voice = profile.voices?.[voiceKey];
    if (!voice) return;
    const out = voiceOutput(Tone, cache, destination, setKey, voiceKey, voice);
    if (out.synth) {
      out.synth.triggerAttackRelease(step.note, step.duration, now, step.vel ?? 1);
    } else {
      const play = INSTRUMENTS[voice.instrument];
      const { note, duration, voice: _voice, ...noteOptions } = step;
      play?.(ctx, out.gain, now, Tone.Frequency(note).toFrequency(), duration, { ...voice.options, ...noteOptions });
    }
    now += step.duration;
  });

  return now;
}
